'use client';

import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';
import { useDebouncedCallback } from 'use-debounce';

import IconClose from '@/fo/icons/base/icons/close.svg';
import IconSearch from '@/fo/icons/base/icons/search.svg';
import {
  searchActions,
  selectQuery,
  selectIsLoading,
  storeSearchSuggestion
} from '@/fo/store/search-suggestion';
import { UiKitInputSearch } from '@/fo/ui/kit/input/search';

import { styles } from './InputSearch.styles';

export interface InputSearchProps {
  autoFocus?: boolean;
  onSearch?: (query: string) => void;
}

export function InputSearch({ autoFocus = true, onSearch }: InputSearchProps) {
  const slots = styles();
  const t = useTranslations();

  const value = useSelector(storeSearchSuggestion, selectQuery);
  const isLoading = useSelector(storeSearchSuggestion, selectIsLoading);

  const debouncedSearch = useDebouncedCallback((query: string) => {
    if (query === '') {
      searchActions.fillDefaultData();
    } else if (query.length > 3) {
      searchActions.search(query);
    }
  }, 200);

  const handleChange = (newValue: string) => {
    storeSearchSuggestion.send({
      type: 'setQuery',
      query: newValue
    });
    debouncedSearch(newValue);
  };

  const handleClear = () => {
    handleChange('');
  };

  const startAdornment = isLoading ? (
    <span className={slots.loading()} />
  ) : (
    <IconSearch
      className={slots.clearSearch()}
      onClick={() => onSearch?.(value)}
    />
  );

  return (
    <UiKitInputSearch
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={t('I want to find')}
      startAdornment={startAdornment}
      endAdornment={
        value && (
          <span
            onClick={handleClear}
            className={slots.clearSearch()}
          >
            <IconClose className={slots.closeSearch()} />
          </span>
        )
      }
      onSearch={onSearch}
      type="header"
    />
  );
}
